import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X, PaperPlaneRight, ArrowClockwise, Quotes, Buildings, Broadcast, ListChecks, ArrowRight,
  Lightning, Trash, Microphone, MicrophoneSlash, Stop, SpeakerHigh, SpeakerSlash, Phone, PhoneSlash,
  ClockCounterClockwise, Plus, CaretLeft, HandTap, Waveform,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { KASARA_OPEN_EVENT, consumePendingKasaraOpen } from "@/lib/kasaraBus";
import { useAuth } from "@/context/AuthContext";
import { useKasaraChat, useKasaraSuggestions, useKasaraConversations, useKasaraBriefing } from "@/lib/queries";
import { Markdown } from "@/components/Markdown";
import kasaraAvatar from "@/assets/kasara-avatar.png";

const REF_ICON = { client: Buildings, signal: Broadcast, output: ListChecks };

function useWorkspaceContext() {
  const location = useLocation();
  const m = location.pathname.match(/^\/workspaces\/([^/?]+)/);
  return { route: location.pathname, workspace_id: m ? m[1] : null };
}

function makeCtx(ref) {
  if (!ref.current) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ref.current = new AC();
  }
  if (ref.current.state === "suspended") ref.current.resume().catch(() => {});
  return ref.current;
}

function forSpeech(t) {
  return (t || "")
    .replace(/\[[A-Z]{1,2}\d+\]/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`#>|]/g, "")
    .replace(/\n+/g, ". ")
    .replace(/\s+/g, " ")
    .trim();
}

// Root-mean-square amplitude (0..~1) from a Web Audio time-domain analyser — used to make the
// avatar + waveform react to real audio (mic input while listening, Kasara's TTS while speaking).
function rmsFromAnalyser(analyser, buf) {
  if (!analyser || !buf) return 0;
  analyser.getByteTimeDomainData(buf);
  let s = 0;
  for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; s += v * v; }
  return Math.sqrt(s / buf.length);
}

// Live "voice bars" visualizer. Heights track the real amplitude (`level`) while `active`;
// a CSS keyframe adds a gentle idle shimmer so it reads as a living, speaking mouth.
function VoiceBars({ level = 0, active = false, className = "", barClassName = "bg-accent-ai" }) {
  const weights = [0.5, 0.85, 1, 0.7, 0.45];
  return (
    <div className={`flex items-center justify-center gap-[3px] ${className}`} aria-hidden="true" data-testid="kasara-voicebars">
      {weights.map((w, i) => {
        const h = active ? Math.max(16, Math.min(100, level * 340 * w)) : 12;
        return (
          <span key={i} className={`w-[3px] rounded-full ${barClassName}`}
            style={{
              height: `${h}%`,
              transformOrigin: "center",
              transition: "height 80ms linear",
              animation: active ? `kasara-bar 820ms ease-in-out ${i * 90}ms infinite` : "none",
            }} />
        );
      })}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Citations({ items, onGo }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2.5 border-t border-border/60 pt-2.5" data-testid="kasara-citations">
      <p className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide text-muted-foreground">
        <Quotes className="h-3 w-3" weight="bold" /> Sources
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {items.map((c) => {
          const Icon = REF_ICON[c.type] || ListChecks;
          return (
            <button key={c.ref} type="button" onClick={() => onGo(c.link)} data-testid={`kasara-citation-${c.ref}`}
              className="group inline-flex max-w-[16rem] items-center gap-1.5 border border-border bg-surface px-2 py-1 text-[11px] text-foreground transition-colors hover:border-primary hover:text-primary">
              <span className="font-mono text-[9px] font-semibold text-primary">{c.ref}</span>
              <Icon className="h-3 w-3 shrink-0 text-muted-foreground group-hover:text-primary" weight="bold" />
              <span className="truncate">{c.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ActionChips({ items, onNavigate, onExecute, busyLabel }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="kasara-actions">
      {items.map((a, i) => {
        const busy = busyLabel === a.label;
        const isExec = a.kind === "execute";
        return (
          <button key={`${a.label}-${i}`} type="button" disabled={busy}
            onClick={() => (isExec ? onExecute(a) : onNavigate(a.route))} data-testid={`kasara-action-${i}`}
            className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase transition-colors disabled:opacity-50 ${isExec ? "border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground" : "border-accent-ai bg-accent-ai/10 text-accent-ai hover:bg-accent-ai hover:text-accent-ai-foreground"}`}>
            <Lightning className={`h-3 w-3 ${busy ? "animate-pulse" : ""}`} weight="fill" /> {a.label}
            {!isExec && <ArrowRight className="h-3 w-3" weight="bold" />}
          </button>
        );
      })}
    </div>
  );
}

function FollowUps({ items, onPick }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2.5 space-y-1" data-testid="kasara-followups">
      {items.map((q, i) => (
        <button key={i} type="button" onClick={() => onPick(q)} data-testid={`kasara-followup-${i}`}
          className="group flex w-full items-center gap-1.5 border border-border bg-card px-2.5 py-1.5 text-left text-[12px] text-foreground transition-colors hover:border-primary hover:text-primary">
          <Lightning className="h-3 w-3 shrink-0 text-muted-foreground group-hover:text-primary" weight="bold" />
          <span>{q}</span>
        </button>
      ))}
    </div>
  );
}

function AssistantMsg({ msg, idx, onGo, onExecute, onFollowUp, onSpeak, speaking, busyLabel }) {
  return (
    <div className="flex gap-2" data-testid="kasara-msg-assistant">
      <div className="relative mt-0.5 h-7 w-7 shrink-0">
        <img src={kasaraAvatar} alt="Kasara"
          className={`h-7 w-7 rounded-full object-cover object-top ${speaking ? "ring-2 ring-accent-ai pulse-ai" : "ring-1 ring-accent-ai/40"}`} />
        {speaking && (
          <span className="absolute -bottom-1 left-1/2 flex h-3 w-7 -translate-x-1/2 items-center justify-center rounded-full bg-shell-text/85 shadow-elev-sm" data-testid="kasara-msg-speaking">
            <VoiceBars level={0.55} active barClassName="bg-white" className="h-1.5" />
          </span>
        )}
      </div>
      <div className="relative min-w-0 flex-1 border border-border bg-card px-3 py-2.5 shadow-elev-sm">
        <button type="button" onClick={() => onSpeak(msg.answer, idx)} data-testid={`kasara-speak-${idx}`}
          title={speaking ? "Stop" : "Read aloud"} aria-label={speaking ? "Stop speaking" : "Read answer aloud"}
          className={`absolute right-2 top-2 transition-colors ${speaking ? "text-accent-ai" : "text-muted-foreground hover:text-primary"}`}>
          {speaking ? <Stop className="h-4 w-4" weight="fill" /> : <SpeakerHigh className="h-4 w-4" weight="bold" />}
        </button>
        {msg.mocked && (
          <span className="mb-1.5 inline-block border border-warning/40 bg-warning/10 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wide text-warning">Mock mode</span>
        )}
        <div className="pr-5"><Markdown content={msg.answer} className="text-[13px]" /></div>
        <Citations items={msg.citations} onGo={onGo} />
        <ActionChips items={msg.actions} onNavigate={onGo} onExecute={onExecute} busyLabel={busyLabel} />
        <FollowUps items={msg.follow_ups} onPick={onFollowUp} />
      </div>
    </div>
  );
}

function HistoryList({ conversations, activeId, onOpen, onDelete, onNew }) {
  return (
    <div className="flex-1 overflow-y-auto px-2 py-2" data-testid="kasara-history-list">
      <button type="button" onClick={onNew} data-testid="kasara-new-chat"
        className="mb-2 flex w-full items-center gap-2 border border-primary bg-primary/10 px-3 py-2 text-[13px] font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
        <Plus className="h-4 w-4" weight="bold" /> New chat
      </button>
      {(!conversations || conversations.length === 0) && (
        <p className="px-2 py-6 text-center text-[12px] text-muted-foreground">No saved conversations yet.</p>
      )}
      {conversations?.map((c, i) => (
        <div key={c.id} data-testid={`kasara-history-item-${i}`}
          className={`group mb-1 flex cursor-pointer items-start gap-2 border px-2.5 py-2 transition-colors ${c.id === activeId ? "border-primary bg-primary/5" : "border-transparent hover:border-border hover:bg-card"}`}
          onClick={() => onOpen(c.id)}>
          <ClockCounterClockwise className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" weight="bold" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-medium text-foreground">{c.title || "New chat"}</p>
            <p className="truncate text-[11px] text-muted-foreground">{c.preview}</p>
          </div>
          <span className="shrink-0 font-mono text-[9px] uppercase text-muted-foreground">{relTime(c.updated_at)}</span>
          <button type="button" onClick={(e) => onDelete(c.id, e)} data-testid={`kasara-history-delete-${i}`}
            title="Delete" className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100">
            <Trash className="h-3.5 w-3.5" weight="bold" />
          </button>
        </div>
      ))}
    </div>
  );
}

const STATUS_LABEL = {
  connecting: "Connecting…",
  listening: "Listening — just speak…",
  transcribing: "Got it…",
  thinking: "Thinking…",
  speaking: "Speaking — talk to interrupt…",
  muted: "Muted",
  "ptt-idle": "Tap to talk",
};

// "Hey Kasara" is optional in a call (the call itself is the explicit conversation) — it's
// politely stripped from the start of an utterance if the user happens to say it.
const WAKE_STRIP_RE = /^\s*(?:hey[\s,]*)?kasara[\s,:.!?-]*/i;

function CallMode({ audioCtxRef, onAsk, onClose }) {
  const [status, setStatus] = useState("connecting");
  const [heard, setHeard] = useState("");
  const [reply, setReply] = useState("");
  const [hint, setHint] = useState("Connecting…");
  const [level, setLevel] = useState(0);          // mic input level (while you speak)
  const [speakLevel, setSpeakLevel] = useState(0); // Kasara's voice level (while it speaks)
  const [muted, setMuted] = useState(false);
  const [pttMode, setPttMode] = useState(false);   // push-to-talk for noisy rooms
  const [pttActive, setPttActive] = useState(false);
  const runningRef = useRef(true);
  const streamRef = useRef(null);    // persistent AEC mic stream (whole call)
  const analyserRef = useRef(null);  // persistent analyser on that stream
  const bufRef = useRef(null);
  const srcRef = useRef(null);       // current TTS buffer source
  const abortTurnRef = useRef(null); // stops the in-flight recording
  const mutedRef = useRef(false);    // when true the mic is gated — Kasara can't hear you
  const pttModeRef = useRef(false);
  const pttActiveRef = useRef(false);
  const pttPressRef = useRef({ t0: 0, startedHere: false });
  const micErrRef = useRef(null);

  // One persistent mic stream w/ echo-cancellation so Kasara can hear you WHILE it speaks (barge-in).
  const initMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const ctx = audioCtxRef.current;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      bufRef.current = new Uint8Array(analyser.fftSize);
      return true;
    } catch (err) { micErrRef.current = err; return false; }
  };

  const readRms = () => {
    const a = analyserRef.current, buf = bufRef.current;
    if (!a || !buf) return 0;
    a.getByteTimeDomainData(buf);
    let s = 0;
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; s += v * v; }
    return Math.sqrt(s / buf.length);
  };

  // Records one utterance off the persistent stream using simple VAD.
  const recordTurn = () => new Promise((resolve) => {
    const stream = streamRef.current;
    if (!stream || !runningRef.current) return resolve(null);
    const mr = new MediaRecorder(stream);
    const chunks = [];
    mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    let started = false, silenceStart = null, raf = null, done = false;
    const START = 0.02, SILENCE_MS = 1100, MAX_MS = 13000, GIVEUP_MS = 12000, t0 = Date.now();
    mr.onstop = () => {
      cancelAnimationFrame(raf);
      resolve(started ? new Blob(chunks, { type: mr.mimeType || "audio/webm" }) : null);
    };
    const finish = () => { if (!done) { done = true; try { mr.stop(); } catch { /* noop */ } } };
    abortTurnRef.current = finish;
    const tick = () => {
      if (done) return;
      if (!runningRef.current) return finish();
      const rms = readRms();
      const now = Date.now();
      setLevel(rms);
      if (rms > START) { started = true; silenceStart = null; }
      else if (started) { if (silenceStart == null) silenceStart = now; else if (now - silenceStart > SILENCE_MS) return finish(); }
      if (started && now - t0 > MAX_MS) return finish();
      if (!started && now - t0 > GIVEUP_MS) return finish();
      raf = requestAnimationFrame(tick);
    };
    mr.start(120);
    raf = requestAnimationFrame(tick);
  });

  // Plays Kasara's reply; resolves true if the user barged in (sustained speech over the mic).
  const speakWithBargeIn = (text) => new Promise(async (resolve) => {
    const clean = forSpeech(text);
    if (!clean) return resolve(false);
    let res;
    try {
      res = await api.post("/kasara/voice/speak", { text: clean.slice(0, 4096) }, { responseType: "arraybuffer", _silent: true });
    } catch { return resolve(false); }
    if (!runningRef.current) return resolve(false);
    const ctx = audioCtxRef.current;
    let audbuf;
    try { audbuf = await ctx.decodeAudioData(res.data.slice(0)); } catch { return resolve(false); }
    const src = ctx.createBufferSource();
    src.buffer = audbuf;
    const outAnalyser = ctx.createAnalyser();
    outAnalyser.fftSize = 256;
    const outBuf = new Uint8Array(outAnalyser.fftSize);
    src.connect(outAnalyser);
    outAnalyser.connect(ctx.destination);
    srcRef.current = src;
    let finished = false, raf = null, loud = 0;
    const t0 = Date.now();
    const GRACE_MS = 500;    // ignore the playback onset transient
    const BARGE_RMS = 0.06;  // > VAD start; AEC removes most of Kasara's own voice
    const BARGE_FRAMES = 10; // ~160ms of sustained user speech to count as interruption
    const done = (didBarge) => {
      if (finished) return;
      finished = true;
      cancelAnimationFrame(raf);
      setSpeakLevel(0);
      try { src.stop(); } catch { /* noop */ }
      if (srcRef.current === src) srcRef.current = null;
      resolve(didBarge);
    };
    src.onended = () => done(false);
    const monitor = () => {
      if (finished) return;
      if (!runningRef.current) return done(false);
      // Avatar + waveform pulse with Kasara's actual voice amplitude.
      setSpeakLevel(rmsFromAnalyser(outAnalyser, outBuf));
      // Barge-in: only while the mic is live (not muted) and past the onset transient.
      if (!mutedRef.current && Date.now() - t0 > GRACE_MS) {
        const rms = readRms();
        if (rms > BARGE_RMS) { loud += 1; if (loud >= BARGE_FRAMES) return done(true); }
        else loud = 0;
      }
      raf = requestAnimationFrame(monitor);
    };
    src.start(0);
    raf = requestAnimationFrame(monitor);
  });

  const stopAll = () => {
    runningRef.current = false;
    try { abortTurnRef.current?.(); } catch { /* noop */ }
    try { srcRef.current?.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Mute/unmute the user's mic mid-call without ending it. Gates the audio track so nothing is
  // captured, and aborts any in-flight turn so Kasara stops listening immediately.
  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    streamRef.current?.getAudioTracks?.().forEach((t) => { t.enabled = !next; });
    if (next) {
      pttActiveRef.current = false; setPttActive(false);
      try { abortTurnRef.current?.(); } catch { /* noop */ }
    }
  };

  // Push-to-talk capture: records only while the user is holding/toggled the Talk button on.
  // Used in noisy rooms where the auto voice-detector would false-trigger.
  const recordPushToTalk = () => new Promise((resolve) => {
    const stream = streamRef.current;
    if (!stream || !runningRef.current) return resolve(null);
    let mr = null, chunks = [], raf = null, done = false, recording = false;
    const finish = () => {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      if (mr && mr.state !== "inactive") { try { mr.stop(); } catch { resolve(null); } }
      else resolve(null);
    };
    abortTurnRef.current = finish;
    const startRec = () => {
      recording = true;
      mr = new MediaRecorder(stream);
      chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = () => resolve(chunks.length ? new Blob(chunks, { type: mr.mimeType || "audio/webm" }) : null);
      mr.start(120);
      setStatus("listening");
      setHint("Listening — tap again or release to send.");
    };
    const tick = () => {
      if (done) return;
      if (!runningRef.current || !pttModeRef.current) return finish();
      if (pttActiveRef.current) {
        if (!recording) startRec();
        setLevel(readRms());
      } else if (recording) {
        return finish(); // push ended → stop & resolve the captured audio
      } else {
        setStatus("ptt-idle");
        setHint("Tap to talk, or press & hold for walkie-talkie.");
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  });

  // Tap = toggle on/off; press-and-hold = record while held, release to send (choice: both).
  const pttDown = () => {
    if (!pttModeRef.current || mutedRef.current) return;
    pttPressRef.current.t0 = Date.now();
    if (!pttActiveRef.current) {
      pttActiveRef.current = true; setPttActive(true);
      pttPressRef.current.startedHere = true;
    } else {
      pttPressRef.current.startedHere = false;
    }
  };
  const pttUp = () => {
    if (!pttModeRef.current) return;
    const dt = Date.now() - pttPressRef.current.t0;
    if (dt >= 250) {                       // held → walkie-talkie → stop on release
      pttActiveRef.current = false; setPttActive(false);
    } else if (!pttPressRef.current.startedHere) { // quick tap while already on → stop
      pttActiveRef.current = false; setPttActive(false);
    } // quick tap that started recording → stay on (toggle)
  };

  const togglePttMode = () => {
    const next = !pttModeRef.current;
    pttModeRef.current = next;
    setPttMode(next);
    pttActiveRef.current = false; setPttActive(false);
    setLevel(0);
    try { abortTurnRef.current?.(); } catch { /* noop */ }
  };

  useEffect(() => {
    runningRef.current = true;
    (async () => {
      setStatus("connecting");
      const ok = await initMic();
      if (!ok) {
        const err = micErrRef.current;
        const inIframe = window.self !== window.top;
        toast.error(err?.name === "NotFoundError"
          ? "No microphone found — connect one to use Call Mode."
          : inIframe
            ? "Mic is blocked in the embedded preview. Open the app in a new browser tab for Call Mode."
            : "Microphone access denied. Allow mic access to use Call Mode.");
        stopAll(); onClose(); return;
      }
      while (runningRef.current) {
        if (mutedRef.current) {
          setStatus("muted");
          setHint("Muted — tap the mic so Kasara can hear you.");
          await new Promise((r) => setTimeout(r, 250));
          continue;
        }
        let blob;
        if (pttModeRef.current) {
          blob = await recordPushToTalk();
        } else {
          setStatus("listening");
          setHint("Just speak — no wake word needed.");
          blob = await recordTurn();
        }
        if (!runningRef.current) break;
        setLevel(0);
        if (mutedRef.current || !blob || blob.size < 1000) continue;
        setStatus("transcribing");
        let text = "";
        try {
          const fd = new FormData();
          fd.append("file", blob, "speech");
          text = (await api.post("/kasara/voice/transcribe", fd, { _silent: true }).then((r) => r.data)).text || "";
        } catch { /* skip turn */ }
        if (!runningRef.current) break;
        if (mutedRef.current) continue;
        // A call is an explicit conversation — respond to whatever was said. "Hey Kasara"
        // is optional and stripped if it leads the utterance.
        const command = text.replace(WAKE_STRIP_RE, "").trim();
        if (!command) continue;
        setHeard(text);
        setHint("");
        setStatus("thinking");
        const d = await onAsk(command);
        if (!runningRef.current) break;
        if (!d) continue;
        setReply(d.answer);
        setStatus("speaking");
        await speakWithBargeIn(d.answer);
        if (!runningRef.current) break;
      }
    })();
    return () => { stopAll(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const speaking = status === "speaking";
  const activeLevel = speaking ? speakLevel : level;
  const ringScale = 1 + Math.min(0.45, activeLevel * 6);
  const avatarScale = speaking ? 1 + Math.min(0.07, speakLevel * 0.9) : 1;
  const waveActive = speaking || status === "listening";
  const idleRing = (status === "listening" || status === "ptt-idle") && level < 0.03;
  const stateColor = status === "muted" ? "bg-muted-foreground"
    : speaking ? "bg-accent-ai" : status === "thinking" ? "bg-warning" : "bg-success";

  return (
    <div className="absolute inset-x-0 bottom-0 top-[57px] z-10 flex flex-col items-center justify-between bg-shell-base px-5 py-6 text-center shell-grain" data-testid="kasara-call-overlay">
      <div className="flex w-full flex-1 flex-col items-center justify-center">
        <div className="relative flex h-32 w-32 items-center justify-center">
          {/* reactive halo — scales with the live voice amplitude */}
          <span className={`absolute h-32 w-32 rounded-full transition-transform duration-100 ${speaking ? "bg-accent-ai/25" : "bg-accent-ai/12"}`}
            style={{ transform: `scale(${ringScale})` }} />
          <span className={`absolute h-28 w-28 rounded-full ${idleRing ? "animate-ping bg-accent-ai/20" : ""}`} />
          <img src={kasaraAvatar} alt="Kasara" data-testid="kasara-call-avatar"
            className={`relative h-24 w-24 rounded-full object-cover object-top ring-4 ring-accent-ai shadow-elev-lg transition-transform duration-75 ${speaking ? "pulse-ai" : ""}`}
            style={{ transform: `scale(${avatarScale})` }} />
          {/* mouth voice-bars — Kasara visibly "talks" in sync with her voice */}
          {speaking && (
            <span className="absolute bottom-1.5 left-1/2 flex h-4 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-shell-text/85 shadow-elev-sm" data-testid="kasara-call-mouth">
              <VoiceBars level={speakLevel} active barClassName="bg-white" className="h-2.5" />
            </span>
          )}
          <span className={`absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full ${stateColor} ring-2 ring-shell-base`}>
            {status === "thinking" && <ArrowClockwise className="h-3 w-3 animate-spin text-white" weight="bold" />}
          </span>
        </div>
        <p className="mt-5 font-heading text-base font-bold text-shell-text">Kasara</p>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-accent-ai" data-testid="kasara-call-status">
          {STATUS_LABEL[status] || "Listening…"}
        </p>
        {/* live waveform — your voice while listening, Kasara's while speaking */}
        <div className="mt-3 h-8 w-44" data-testid="kasara-call-waveform">
          <VoiceBars level={activeLevel} active={waveActive} className="h-full"
            barClassName={speaking ? "bg-accent-ai" : "bg-success"} />
        </div>
        {hint && <p className="mt-2 max-w-xs text-[12px] text-shell-muted">{hint}</p>}
        {heard && (
          <div className="mt-3 max-w-sm" data-testid="kasara-call-transcript">
            <p className="font-mono text-[9px] uppercase tracking-wide text-shell-muted">You said</p>
            <p className="mt-0.5 text-[13px] text-shell-text">“{heard}”</p>
          </div>
        )}
        {reply && status !== "thinking" && (
          <p className="mt-3 line-clamp-3 max-w-sm text-[12px] text-shell-muted" data-testid="kasara-call-reply">{forSpeech(reply).slice(0, 220)}</p>
        )}
      </div>

      <div className="flex w-full flex-col items-center gap-3">
        {pttMode && (
          <button type="button" data-testid="kasara-ptt-talk"
            onPointerDown={pttDown} onPointerUp={pttUp} onPointerLeave={pttUp} onPointerCancel={pttUp}
            aria-pressed={pttActive} title="Tap to talk, or press & hold"
            className={`inline-flex select-none items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-elev-md transition-transform hover:scale-105 ${pttActive ? "scale-105 bg-success" : "bg-accent-ai"}`}
            style={{ touchAction: "none" }}>
            <Microphone className="h-5 w-5" weight="fill" />
            {pttActive ? "Listening — release to send" : "Hold or tap to talk"}
          </button>
        )}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button type="button" onClick={togglePttMode} data-testid="kasara-call-ptt-toggle"
            aria-pressed={pttMode} title={pttMode ? "Switch to auto-listen" : "Switch to push-to-talk (better in noisy rooms)"}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-elev-md transition-transform hover:scale-105 ${pttMode ? "bg-accent-ai text-white" : "bg-shell-surface text-shell-text ring-1 ring-shell-border"}`}>
            {pttMode ? <HandTap className="h-5 w-5" weight="fill" /> : <Waveform className="h-5 w-5" weight="fill" />}
            {pttMode ? "Push-to-talk" : "Auto"}
          </button>
          <button type="button" onClick={toggleMute} data-testid="kasara-call-mute"
            aria-pressed={muted} title={muted ? "Unmute microphone" : "Mute microphone"}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-elev-md transition-transform hover:scale-105 ${muted ? "bg-warning text-white" : "bg-shell-surface text-shell-text ring-1 ring-shell-border"}`}>
            {muted ? <MicrophoneSlash className="h-5 w-5" weight="fill" /> : <Microphone className="h-5 w-5" weight="fill" />}
            {muted ? "Unmute" : "Mute"}
          </button>
          <button type="button" onClick={() => { stopAll(); onClose(); }} data-testid="kasara-call-end"
            className="inline-flex items-center gap-2 rounded-full bg-destructive px-6 py-3 text-sm font-semibold text-white shadow-elev-md transition-transform hover:scale-105">
            <PhoneSlash className="h-5 w-5" weight="fill" /> End call
          </button>
        </div>
      </div>
    </div>
  );
}

function Panel({ ctx, audioCtxRef, onClose }) {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busyLabel, setBusyLabel] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("kasara.voice") !== "off");
  const [view, setView] = useState("chat");
  const [callOpen, setCallOpen] = useState(false);
  const { user } = useAuth();
  const isClient = user?.user_type === "client";
  const chat = useKasaraChat();
  const { data: sugg } = useKasaraSuggestions(ctx.workspace_id);
  const { data: convData, refetch: refetchConvs } = useKasaraConversations();
  const { data: brief } = useKasaraBriefing();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const endRef = useRef(null);
  const recorderRef = useRef(null);
  const srcRef = useRef(null);
  const voiceOnRef = useRef(voiceOn);
  const initedRef = useRef(false);
  const conversations = convData?.conversations || [];

  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, chat.isPending]);
  useEffect(() => () => { stopAudio(); recorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop()); }, []); // eslint-disable-line

  // On open: resume most recent conversation for continuity.
  useEffect(() => {
    if (initedRef.current || !convData) return;
    initedRef.current = true;
    if (conversations.length) loadConversation(conversations[0].id);
  }, [convData]); // eslint-disable-line

  // Proactive spoken to-do briefing, once per browser session.
  useEffect(() => {
    if (brief?.text && voiceOnRef.current && !sessionStorage.getItem("kasara.briefed")) {
      sessionStorage.setItem("kasara.briefed", "1");
      speak(brief.text, "auto");
    }
  }, [brief]); // eslint-disable-line

  const stopAudio = () => {
    try { srcRef.current?.stop(); } catch { /* noop */ }
    srcRef.current = null;
    setSpeakingIdx(null);
  };

  const speak = async (text, idx) => {
    const auto = idx === "auto";
    if (speakingIdx === idx && !auto) { stopAudio(); return; }
    const clean = forSpeech(text);
    if (!clean) return;
    const actx = makeCtx(audioCtxRef);
    if (!actx) { if (!auto) toast.error("Audio isn't supported in this browser."); return; }
    try {
      stopAudio();
      const res = await api.post("/kasara/voice/speak", { text: clean.slice(0, 4096) }, { responseType: "arraybuffer", _silent: true });
      const audbuf = await actx.decodeAudioData(res.data.slice(0));
      const src = actx.createBufferSource();
      src.buffer = audbuf;
      src.connect(actx.destination);
      src.onended = () => { if (srcRef.current === src) { srcRef.current = null; setSpeakingIdx(null); } };
      srcRef.current = src;
      setSpeakingIdx(idx);
      src.start(0);
    } catch {
      if (!auto) toast.error("Couldn't play audio. Please try again.");
      setSpeakingIdx(null);
    }
  };

  const loadConversation = async (id) => {
    setView("chat");
    try {
      const c = await api.get(`/kasara/conversations/${id}`).then((r) => r.data);
      setConversationId(c.id);
      setMessages((c.messages || []).map((m) =>
        m.role === "user"
          ? { role: "user", content: m.content }
          : { role: "assistant", answer: m.content, citations: m.citations, actions: m.actions, follow_ups: m.follow_ups, mocked: m.mocked }));
    } catch { toast.error("Couldn't load that conversation."); }
  };

  const newChat = () => { stopAudio(); setConversationId(null); setMessages([]); setView("chat"); };

  const removeConversation = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/kasara/conversations/${id}`);
      refetchConvs();
      if (id === conversationId) newChat();
    } catch { toast.error("Couldn't delete that conversation."); }
  };

  // Core ask: persists + appends to thread, returns the answer payload (used by chat + Call Mode).
  const ask = async (text) => {
    const q = (text || "").trim();
    if (!q) return null;
    makeCtx(audioCtxRef);
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    try {
      const d = await chat.mutateAsync({ message: q, conversationId, context: ctx });
      setConversationId(d.conversation_id);
      setMessages((prev) => [...prev, { role: "assistant", ...d }]);
      qc.invalidateQueries({ queryKey: ["kasara-conversations"] });
      return d;
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", answer: "Sorry — I hit a snag reaching the platform brain. Please try again." }]);
      return null;
    }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || chat.isPending) return;
    setInput("");
    const d = await ask(q);
    if (d && voiceOnRef.current) speak(d.answer, "auto");
  };

  const go = (route) => { if (route) { navigate(route); onClose(); } };

  const onExecute = async (a) => {
    setBusyLabel(a.label);
    try {
      if (a.action === "draft_advisory") {
        const d = await api.post(`/radar/signals/${a.params.signal_id}/advisory`, { workspace_id: a.params.workspace_id }).then((r) => r.data);
        toast.success("Advisory drafted — opening for review"); qc.invalidateQueries({ queryKey: ["outputs"] }); go(`/outputs/${d.output_id}`);
      } else if (a.action === "create_workspace") {
        const d = await api.post("/workspaces", { name: a.params.name, sector: a.params.sector || "" }).then((r) => r.data);
        toast.success(`Workspace "${d.name}" created`); qc.invalidateQueries({ queryKey: ["workspaces"] }); go(`/workspaces/${d.id}`);
      } else if (a.action === "start_assessment") {
        await api.post(`/workspaces/${a.params.workspace_id}/assessments`, { name: a.params.name || "New Assessment", framework: a.params.framework || "DUCF" });
        toast.success("Assessment created — opening Assessments"); go(`/workspaces/${a.params.workspace_id}?tab=assessments`);
      } else if (a.action === "approve_output") {
        await api.post(`/outputs/${a.params.output_id}/review`, { review_status: "Approved" });
        toast.success("Output approved"); qc.invalidateQueries({ queryKey: ["outputs"] }); qc.invalidateQueries({ queryKey: ["nav-badges"] }); go(`/outputs/${a.params.output_id}`);
      } else if (a.action === "respond_request") {
        await api.post(`/workspaces/${a.params.workspace_id}/audit-requests/${a.params.request_id}/messages`, { body: a.params.message });
        toast.success("Response sent — your DAAKYI team has been notified");
        qc.invalidateQueries({ queryKey: ["audit-requests", a.params.workspace_id] });
        go("/portal?tab=audit-requests");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyLabel(null);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice input isn't supported in this browser."); return;
    }
    makeCtx(audioCtxRef);
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (err) {
      const inIframe = window.self !== window.top;
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        toast.error(inIframe ? "Mic is blocked in the embedded preview. Open the app in a new browser tab to talk to Kasara." : "Microphone permission denied. Allow mic access, then try again.");
      } else if (err?.name === "NotFoundError" || err?.name === "OverconstrainedError") {
        toast.error("No microphone was found on this device.");
      } else { toast.error("Couldn't access the microphone."); }
      return;
    }
    try {
      const mr = new MediaRecorder(stream);
      mr.stream = stream;
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 800) { toast.message("Didn't catch that — hold the mic a little longer."); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "speech");
          const { text } = await api.post("/kasara/voice/transcribe", fd, { _silent: true }).then((r) => r.data);
          if (text) send(text); else toast.message("Didn't catch that — try again.");
        } catch { toast.error("Couldn't transcribe audio. Please try again."); }
        finally { setTranscribing(false); }
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      toast.error("Couldn't start recording.");
    }
  };

  const toggleRecord = () => { if (recording) recorderRef.current?.stop(); else startRecording(); };

  const toggleVoice = () => {
    makeCtx(audioCtxRef);
    setVoiceOn((v) => {
      const next = !v;
      localStorage.setItem("kasara.voice", next ? "on" : "off");
      if (!next) stopAudio();
      return next;
    });
  };

  const openCall = () => { stopAudio(); makeCtx(audioCtxRef); setView("chat"); setCallOpen(true); };

  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div data-testid="kasara-panel" className="fixed bottom-[96px] right-4 z-50 flex h-[min(72vh,640px)] w-[calc(100vw-2rem)] flex-col border border-border bg-background shadow-elev-lg sm:right-6 sm:w-[400px]">
      <div className="flex items-center gap-1.5 border-b border-border bg-shell-base px-3 py-2.5 shell-grain">
        {view === "history" ? (
          <button type="button" onClick={() => setView("chat")} data-testid="kasara-history-back" className="text-shell-muted hover:text-shell-text" title="Back">
            <CaretLeft className="h-4 w-4" weight="bold" />
          </button>
        ) : (
          <div className="relative h-9 w-9 shrink-0">
            <img src={kasaraAvatar} alt="Kasara"
              className={`h-9 w-9 rounded-full object-cover object-top ${speakingIdx !== null ? "ring-2 ring-accent-ai pulse-ai" : "ring-1 ring-accent-ai"}`} />
            {speakingIdx !== null && (
              <span className="absolute -bottom-1 left-1/2 flex h-3.5 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-shell-text/85 shadow-elev-sm" data-testid="kasara-header-speaking">
                <VoiceBars level={0.6} active barClassName="bg-white" className="h-2" />
              </span>
            )}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-bold leading-none text-shell-text">{view === "history" ? "History" : "Kasara"}</p>
          {view !== "history" && (
            <p className="mt-0.5 truncate font-mono text-[9px] uppercase tracking-wide text-shell-muted" data-testid="kasara-context-badge">
              {isClient ? "Your engagement copilot" : ctx.workspace_id ? "Scoped to this client" : "Platform copilot"}
            </p>
          )}
        </div>
        <button type="button" onClick={openCall} data-testid="kasara-call-btn" title="Voice call (hands-free)" className="text-success transition-colors hover:opacity-80">
          <Phone className="h-4 w-4" weight="fill" />
        </button>
        <button type="button" onClick={toggleVoice} data-testid="kasara-voice-toggle" aria-pressed={voiceOn}
          title={voiceOn ? "Voice on — Kasara reads answers aloud" : "Voice off"}
          className={`transition-colors ${voiceOn ? "text-accent-ai" : "text-shell-muted hover:text-shell-text"}`}>
          {voiceOn ? <SpeakerHigh className="h-4 w-4" weight="fill" /> : <SpeakerSlash className="h-4 w-4" weight="bold" />}
        </button>
        <button type="button" onClick={newChat} data-testid="kasara-new-chat-btn" title="New chat" className="text-shell-muted transition-colors hover:text-shell-text">
          <Plus className="h-4 w-4" weight="bold" />
        </button>
        <button type="button" onClick={() => setView(view === "history" ? "chat" : "history")} data-testid="kasara-history-btn" title="History" className={`transition-colors ${view === "history" ? "text-accent-ai" : "text-shell-muted hover:text-shell-text"}`}>
          <ClockCounterClockwise className="h-4 w-4" weight="bold" />
        </button>
        <button type="button" onClick={onClose} data-testid="kasara-close" title="Close" aria-label="Close Kasara" className="text-shell-muted transition-colors hover:text-shell-text">
          <X className="h-4 w-4" weight="bold" />
        </button>
      </div>

      {callOpen && <CallMode audioCtxRef={audioCtxRef} onAsk={ask} onClose={() => setCallOpen(false)} />}

      {view === "history" ? (
        <HistoryList conversations={conversations} activeId={conversationId} onOpen={loadConversation} onDelete={removeConversation} onNew={newChat} />
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3" data-testid="kasara-thread">
            {messages.length === 0 && !chat.isPending && (
              <div data-testid="kasara-empty">
                {brief?.text && (
                  <div className="mb-3 border-l-2 border-accent-ai bg-accent-ai/5 px-3 py-2 text-[12px] leading-relaxed text-foreground" data-testid="kasara-briefing">
                    {brief.text}
                  </div>
                )}
                <p className="text-[13px] leading-relaxed text-foreground">
                  {isClient ? (
                    <>Hi, I'm <span className="font-semibold">Kasara</span>. Ask me about your deliverables, security
                    posture, open requests or anything in your portal. Type, tap the mic, or start a hands-free <span className="font-semibold">voice call</span>.</>
                  ) : (
                    <>Hi, I'm <span className="font-semibold">Kasara</span>. Ask me how the platform works, about your clients
                    &amp; threats, or to take you somewhere. Type, tap the mic, or start a hands-free <span className="font-semibold">voice call</span>.</>
                  )}
                </p>
                <div className="mt-3 space-y-1.5">
                  {(sugg?.suggestions || []).map((q, i) => (
                    <button key={i} type="button" onClick={() => send(q)} data-testid={`kasara-suggestion-${i}`}
                      className="group flex w-full items-center gap-1.5 border border-border bg-card px-2.5 py-1.5 text-left text-[12px] text-foreground transition-colors hover:border-primary hover:text-primary">
                      <Lightning className="h-3 w-3 shrink-0 text-accent-ai" weight="bold" />
                      <span>{q}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="flex justify-end" data-testid="kasara-msg-user">
                  <div className="max-w-[85%] border border-primary bg-primary px-3 py-2 text-[13px] text-primary-foreground">{m.content}</div>
                </div>
              ) : (
                <AssistantMsg key={i} msg={m} idx={i} onGo={go} onExecute={onExecute}
                  onFollowUp={send} onSpeak={speak} speaking={speakingIdx === i} busyLabel={busyLabel} />
              )
            )}
            {chat.isPending && (
              <div className="flex items-center gap-2 text-muted-foreground" data-testid="kasara-thinking">
                <img src={kasaraAvatar} alt="" className="h-6 w-6 animate-pulse rounded-full object-cover object-top" />
                <span className="font-mono text-[10px] uppercase tracking-wide">Thinking…</span>
              </div>
            )}
            {transcribing && (
              <div className="flex items-center gap-2 text-muted-foreground" data-testid="kasara-transcribing">
                <ArrowClockwise className="h-4 w-4 animate-spin text-accent-ai" weight="bold" />
                <span className="font-mono text-[10px] uppercase tracking-wide">Transcribing…</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t border-border bg-background p-2">
            {recording && (
              <p className="mb-1 flex items-center gap-1.5 px-1 font-mono text-[10px] uppercase tracking-wide text-destructive" data-testid="kasara-recording-indicator">
                <span className="h-2 w-2 animate-pulse rounded-full bg-destructive" /> Listening — tap stop when done
              </p>
            )}
            <div className="flex items-end gap-1.5 border border-border bg-card p-1.5 focus-within:border-primary">
              <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1}
                placeholder={recording ? "Listening…" : "Ask Kasara, or tap the mic…"} data-testid="kasara-input"
                className="max-h-28 min-h-[2rem] flex-1 resize-none bg-transparent px-1.5 py-1 text-[13px] text-foreground outline-none placeholder:text-muted-foreground" />
              <button type="button" onClick={toggleRecord} disabled={transcribing || chat.isPending} data-testid="kasara-mic"
                title={recording ? "Stop recording" : "Talk to Kasara"} aria-label={recording ? "Stop recording" : "Record voice"}
                className={`inline-flex h-8 w-8 items-center justify-center border transition-colors disabled:opacity-40 ${recording ? "animate-pulse border-destructive bg-destructive text-white" : "border-border bg-surface text-muted-foreground hover:border-accent-ai hover:text-accent-ai"}`}>
                {recording ? <Stop className="h-4 w-4" weight="fill" /> : <Microphone className="h-4 w-4" weight="bold" />}
              </button>
              <button type="button" onClick={() => send()} disabled={!input.trim() || chat.isPending} data-testid="kasara-send"
                className="inline-flex h-8 w-8 items-center justify-center bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40">
                {chat.isPending ? <ArrowClockwise className="h-4 w-4 animate-spin" weight="bold" /> : <PaperPlaneRight className="h-4 w-4" weight="fill" />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function KasaraWidget() {
  const [open, setOpen] = useState(false);
  const ctx = useWorkspaceContext();
  const audioCtxRef = useRef(null);
  const toggle = () => { if (!open) makeCtx(audioCtxRef); setOpen((v) => !v); };
  useEffect(() => {
    const onOpen = () => { consumePendingKasaraOpen(); makeCtx(audioCtxRef); setOpen(true); };
    window.addEventListener(KASARA_OPEN_EVENT, onOpen);
    if (consumePendingKasaraOpen()) onOpen();
    return () => window.removeEventListener(KASARA_OPEN_EVENT, onOpen);
  }, []);
  return (
    <>
      {open && <Panel ctx={ctx} audioCtxRef={audioCtxRef} onClose={() => setOpen(false)} />}
      <button type="button" onClick={toggle} data-testid="kasara-launcher"
        aria-label={open ? "Close Kasara" : "Open Kasara copilot"} className="group fixed bottom-5 right-4 z-50 sm:right-6">
        <span className="relative block">
          <img src={kasaraAvatar} alt="Kasara" className="h-16 w-16 rounded-full object-cover object-top shadow-elev-lg ring-2 ring-accent-ai transition-transform group-hover:scale-105" />
          {open ? (
            <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-background shadow-elev-sm">
              <X className="h-3.5 w-3.5" weight="bold" />
            </span>
          ) : (
            <>
              <span className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border-2 border-background bg-success" />
              <span aria-hidden className="absolute inset-0 animate-ping rounded-full ring-2 ring-accent-ai/40" />
            </>
          )}
        </span>
      </button>
    </>
  );
}
